import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import api from "@/lib/api";
import BricksLoader from "@/components/BricksLoader";

/** Google Calendar OAuth lands here (https://app.brxs.ca/auth/google-calendar).
 *  Direct Google code+state (client secret on API) or Supabase PKCE with extra calendar scope.
 */

function isDirectGoogleState(state) {
  return /^[0-9a-f-]{36}\./i.test(state || "");
}

async function consumeSupabaseSession() {
  const { data: { session: existing } } = await supabase.auth.getSession();
  if (existing?.provider_token || existing?.provider_refresh_token) return existing;

  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  const pkceCode = search.get("code");
  if (pkceCode && !isDirectGoogleState(search.get("state") || "")) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(pkceCode);
    if (!error && data?.session) return data.session;
  }

  const access_token = hash.get("access_token");
  const refresh_token = hash.get("refresh_token");
  if (access_token && refresh_token) {
    const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
    if (!error && data?.session) return data.session;
  }

  for (let i = 0; i < 10; i++) {
    const { data } = await supabase.auth.getSession();
    const s = data?.session;
    if (s?.provider_token || s?.provider_refresh_token) return s;
    if (s?.access_token && i > 3) return s;
    await new Promise((r) => setTimeout(r, 200));
  }
  return existing || null;
}

export default function GoogleCalendarCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const go = (q) => navigate(`/integrations?google=${q}`, { replace: true });

    (async () => {
      const search = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const err = search.get("error") || hash.get("error");
      const code = search.get("code");
      const state = search.get("state") || "";

      if (err) return go("denied");

      if (code && isDirectGoogleState(state)) {
        let session = null;
        for (let i = 0; i < 8; i++) {
          const { data } = await supabase.auth.getSession();
          session = data?.session;
          if (session?.access_token) break;
          await new Promise((r) => setTimeout(r, 200));
        }
        if (!session?.access_token) return go("error");
        try {
          const res = await api.post("/integrations/google-calendar/complete", { code, state });
          if (res.data?.status === "connected") return go("connected");
          return go("error");
        } catch (e) {
          const d = e?.response?.data?.detail;
          const msg = typeof d === "string" ? d.toLowerCase() : "";
          if (msg.includes("permission") || msg.includes("scope")) return go("noscope");
          return go("error");
        }
      }

      const session = await consumeSupabaseSession();
      if (!session?.access_token) return go("error");

      const providerToken = session.provider_token;
      const providerRefresh = session.provider_refresh_token;
      if (!providerRefresh) return go("noscope");

      try {
        const res = await api.post("/integrations/google-calendar/complete-session", {
          provider_token: providerToken || "",
          provider_refresh_token: providerRefresh,
        });
        if (res.data?.status === "connected") return go("connected");
        return go("error");
      } catch (e) {
        const d = e?.response?.data?.detail;
        const msg = typeof d === "string" ? d.toLowerCase() : "";
        if (msg.includes("permission") || msg.includes("scope")) return go("noscope");
        return go("error");
      }
    })();
  }, [navigate]);

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <BricksLoader variant="assemble" size={64} />
    </div>
  );
}
